// src/features/benchmark/components/BenchmarkPage.tsx
//
// "Internal benchmark (ranking + median marker)" (w7.u5, CF S6/D22: a chain
// tier feature) - route /benchmark. GET /api/v2/ui/benchmark (w7.u1)
// returns chain.ranking (siblings of the same organisation, ordered by
// containment desc - V2MetricEngine's own sibling proxy, w2.u5's
// documented simplification, not a real chain/brand hierarchy), chain.median
// and chain.properties_with_rates. The ranking table marks the row where
// the chain median falls with a thin divider, per the design's own "ranking
// + median marker" (never a chart to read). A user who sees one hotel of the
// chain gets `scope: "own"`: the API sends only that hotel's row, so the page
// shows the hotel against the median instead of a one-row ranking.
import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { EmptyState } from '@/components/common/EmptyState';
import { WidgetCard } from '@/components/common/WidgetCard';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { MetricStamp, remedyKey } from '@/features/insight-v2-shared/components/MetricStamp';
import { formatV2Share } from '@/lib/analyticsV2Format';
import { metricLabel } from '@/lib/v2MetricView';
import { rankNumbers } from '@/lib/benchmarkView';
import { DASH } from '@/lib/analyticsFormat';
import type { BenchmarkRankingRow, BenchmarkResponse, MetricResultDto } from '@/types/analyticsV2';

/** The sentence of a remedy code, or the dash when there is none. */
const remedyText = (code: string | null | undefined, translate: (key: string, options?: object) => string): string =>
  code ? translate(`resources.insightV2.remedies.${remedyKey(code)}`, { _: DASH, minutes: 0 }) : DASH;

const RowFigure: React.FC<{ row: BenchmarkRankingRow }> = ({ row }) => {
  const translate = useTranslate();
  if (row.containment !== null) return <>{formatV2Share(row.containment)}</>;
  return (
    <Typography variant="body2" color="text.secondary">
      {remedyText(row.remedy, translate)}
    </Typography>
  );
};

export const BenchmarkPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('benchmark');
  const exportCsv = useCsvExport({ page: 'benchmark' });
  const canExport = hasResourcePermission('benchmark', 'export');

  const { propertyId, properties } = property;

  // The ranking row carries the property's id and short code only; the name
  // comes from the same property list the selector shows. The short code is
  // the fallback for a sibling this user cannot open.
  const propertyName = React.useCallback(
    (row: BenchmarkRankingRow): string =>
      properties.find((p) => p.id === row.property_id)?.displayName ??
      properties.find((p) => p.shortCode === row.short_code)?.displayName ??
      row.short_code,
    [properties]
  );
  const [period, setPeriod] = useAnalyticsPeriod();

  const { data, loading, error, refresh } = useAnalyticsFetch<BenchmarkResponse>(
    () => (dataProvider as any).getAnalyticsBenchmark({ property: propertyId, from: period.from, to: period.to }),
    [dataProvider, propertyId, period.from, period.to],
    Boolean(propertyId)
  );

  const byId = React.useMemo(() => {
    const map = new Map<string, MetricResultDto>();
    (data?.metrics ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [data]);
  const ownView = data?.scope === 'own';

  const ranking = byId.get('chain.ranking');
  const median = byId.get('chain.median');
  const withRates = byId.get('chain.properties_with_rates');
  const rows = (ranking?.value as BenchmarkRankingRow[] | undefined) ?? [];
  const medianValue = !median || median.absent ? null : (median.value as number);

  // First row (from the top, ranking is desc) whose own containment is at or
  // below the chain median - the marker sits above that row.
  const medianRowIndex = medianValue === null ? -1 : rows.findIndex((r) => r.containment !== null && r.containment <= medianValue);

  const medianLabel = medianValue === null ? DASH : formatV2Share(medianValue);
  const medianReason = median && medianValue === null ? remedyText(median.stamp.remedies[0], translate) : null;
  const ranks = rankNumbers(rows);
  // «Su hotel» or «Sus hoteles» by how many hotels the user sees, not by scope.
  const manyOwn = (data?.visibleHotels ?? rows.length) > 1;

  const handleExport = async () => {
    if (rows.length === 0) return;
    await exportCsv(
      ownView ? [...rows, { property_id: '', short_code: translate('resources.benchmark.own.median'), containment: medianValue, remedy: null }] : rows,
      [
        { labelKey: 'resources.benchmark.export.property', value: (r: BenchmarkRankingRow) => (r.property_id ? propertyName(r) : r.short_code) },
        { labelKey: 'resources.benchmark.export.containment', value: (r: BenchmarkRankingRow) => (r.containment === null ? '' : formatV2Share(r.containment)) },
        { labelKey: 'resources.benchmark.export.remedy', value: (r: BenchmarkRankingRow) => (r.remedy ? translate(`resources.insightV2.remedies.${remedyKey(r.remedy)}`, { _: '' }) : '') },
      ],
      { from: period.from, to: period.to }
    );
  };

  return (
    <Box>
      <SectionHeader manualScreen="benchmark"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate('resources.benchmark.subtitle')}
        helpConfig={{
          titleKey: 'resources.benchmark.help.title',
          descriptionKey: 'resources.benchmark.help.description',
          featureKeys: ['resources.benchmark.help.features.list'],
          tipKeys: ['resources.benchmark.help.tips.property'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && <FetchErrorBanner onRetry={refresh} />}

          <WidgetCard title={translate('resources.benchmark.summary.title')} sx={{ mb: 2.5 }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">{DASH}</Typography>
            ) : (
              <Typography variant="body2">
                {withRates && !withRates.absent
                  ? translate('resources.benchmark.summary.withRates', {
                      count: (withRates.value as Record<string, unknown>).with_rates,
                      total: (withRates.value as Record<string, unknown>).total,
                    })
                  : DASH}
              </Typography>
            )}
          </WidgetCard>

          {ownView ? (
            <WidgetCard title={translate(manyOwn ? 'resources.benchmark.own.titleMany' : 'resources.benchmark.own.title')}>
              {loading ? (
                <Typography variant="body2" color="text.secondary">{DASH}</Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('resources.benchmark.ranking.property')}</TableCell>
                        <TableCell align="right">{translate('resources.benchmark.ranking.containment')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.property_id} selected={row.property_id === propertyId}>
                          <TableCell>{propertyName(row)}</TableCell>
                          <TableCell align="right">
                            <RowFigure row={row} />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ borderTop: 2, borderColor: 'divider', bgcolor: 'action.hover', fontWeight: 600 }}>
                          {translate('resources.benchmark.own.median')}
                        </TableCell>
                        <TableCell align="right" sx={{ borderTop: 2, borderColor: 'divider', bgcolor: 'action.hover', fontWeight: 600 }}>
                          {medianReason ? (
                            <Typography variant="body2" color="text.secondary">
                              {medianReason}
                            </Typography>
                          ) : (
                            medianLabel
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                    {translate(manyOwn ? 'resources.benchmark.own.noteMany' : 'resources.benchmark.own.note')}
                  </Typography>
                  {ranking && <MetricStamp stamp={ranking.stamp} sx={{ mt: 1 }} />}
                </>
              )}
            </WidgetCard>
          ) : (
            <WidgetCard title={ranking ? metricLabel(ranking, translate) : translate('resources.benchmark.ranking.title')}>
              {loading ? (
                <Typography variant="body2" color="text.secondary">{DASH}</Typography>
              ) : ranking?.absent ? (
                <MetricStamp stamp={ranking.stamp} />
              ) : rows.length === 0 ? (
                <EmptyState message={translate('resources.benchmark.ranking.empty')} minHeight={160} />
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{translate('resources.benchmark.ranking.property')}</TableCell>
                        <TableCell align="right">{translate('resources.benchmark.ranking.containment')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <React.Fragment key={row.property_id}>
                          {index === medianRowIndex && (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 0.5, borderTop: 2, borderColor: 'divider', bgcolor: 'action.hover' }}>
                                <Typography variant="caption" fontWeight={600}>
                                  {translate('resources.benchmark.ranking.medianMarker', { value: medianLabel })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow selected={row.property_id === propertyId}>
                            <TableCell>{ranks[index] ?? ''}</TableCell>
                            <TableCell>{propertyName(row)}</TableCell>
                            <TableCell align="right">
                              <RowFigure row={row} />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  {medianReason && (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                      {translate('resources.benchmark.ranking.medianMissing', { reason: medianReason })}
                    </Typography>
                  )}
                  {ranking && <MetricStamp stamp={ranking.stamp} sx={{ mt: 1.5 }} />}
                </>
              )}
            </WidgetCard>
          )}
        </>
      )}
    </Box>
  );
};

export default BenchmarkPage;
